import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthApi } from '../../features/auth/data/auth-api';
import { UserStore } from '../store/user.store';
import { Auth } from './auth';
import { refreshAccessToken } from './refresh-session';

// Requests the refresh flow itself depends on — retrying them on 401 would loop.
const AUTH_EXEMPT_PATHS = ['/api/v1/auth/potato/callback', '/api/v1/auth/refresh'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(Auth);
  const authApi = inject(AuthApi);
  const userStore = inject(UserStore);
  const router = inject(Router);

  const accessToken = auth.getAccessToken();
  const authorizedReq = accessToken
    ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
    : req;

  return next(authorizedReq).pipe(
    catchError((error: unknown) => {
      const isExempt = AUTH_EXEMPT_PATHS.some((path) => req.url.includes(path));
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || isExempt) {
        return throwError(() => error);
      }

      return from(refreshAccessToken(authApi, auth)).pipe(
        switchMap((newAccessToken) => {
          if (!newAccessToken) {
            userStore.clearSession();
            void router.navigateByUrl('/login');
            return throwError(() => error);
          }

          const session = auth.getSession();
          if (session) {
            userStore.setSession(session, session.token);
          }

          return next(req.clone({ setHeaders: { Authorization: `Bearer ${newAccessToken}` } }));
        })
      );
    })
  );
};
